import {
    chainWalk, checkInactiveOrDeprecatedAsset,
    convertTableJsonToAsset, deepCopyObject, getAliasFromParent, getAssetLabelFromMac,
    getNodeId,
    isResponseCode5xx,
    logOperationalMetric, setAliasToRequest
} from "../../../util/Util";
import {
    ACTIVE,
    ALERT_ASSET_STATUS_MESSAGE,
    ALERT_UPDATE_ASSET_MESSAGE,
    ALERT_UPDATE_ASSET_MESSAGE_FOR_ACTIVE_ASSET, ALERT_UPDATE_SUCCESS_ASSET_MESSAGE,
    DEFAULT_VENDOR_ATTRIBUTES, DEPRECATED, getUpdateAssetMessageAlertWithSite,
    LOADING_STRING,
    ZANCASORT_ASSET_TYPE
} from "../../../util/Constants";
import {
    addAsset,
    getAssetByAssetLabel,
    getAssets,
    setAssetStatus,
    updateAsset,
    updateAssetInPlace
} from "../../../resources/AssetResource";
import {updateConfigurationsInZancaSortAssetObject} from "../datamodel/ZancaSortAssetModelResource";

const metricProcessName = "ZANCASORT_DEVICE_DETAILS";
export function fetchZancaSortDeviceDetails(dispatcher) {
    const request = {
        nodeId: getNodeId(),
        assetType: ZANCASORT_ASSET_TYPE
    }
    getZancaSortDeviceDetails(dispatcher, request)
}

export function setZancaSortDeviceDetails(dispatcher, stateValues, showCustomAlert) {
    stateValues.mac = stateValues.mac.toLowerCase();
    const request = convertTableJsonToAsset(stateValues, ZANCASORT_ASSET_TYPE, ACTIVE);
    addZancaSortDeviceDetails(dispatcher, request, showCustomAlert)
}

export function updateZancaSortDeviceDetails(dispatcher, stateValues, showCustomAlert) {
    const request = convertTableJsonToAsset(stateValues, ZANCASORT_ASSET_TYPE, ACTIVE);
    updateZancaSortAsset(dispatcher, request, showCustomAlert);
}

export function deleteZancaSortDeviceDetails(dispatcher, mac) {
    const request = {
        assetLabel: {
            type: "MAC_ADDRESS",
            value: mac
        },
        assetStatus: DEPRECATED
    }
    setAssetLabelDeprecatedZancaSortDeviceDetails(dispatcher, request)
}

export async function updateZancaSortDeviceMac(dispatcher, stateValues, mac, showCustomAlert) {
    const startTime = Date.now();
    const assetLabel = getAssetLabelFromMac(stateValues.mac.toLowerCase());
    const assetResult = await getAssetByAssetLabel(assetLabel);
    const nodeId = chainWalk(() => assetResult.data.asset.assetDetails.nodeId, null);
    const assetStatus = chainWalk(() => assetResult.data.asset.assetDetails.assetStatus, null);
    if (assetStatus && !checkInactiveOrDeprecatedAsset(assetStatus)) {
        showCustomAlert(getUpdateAssetMessageAlertWithSite(nodeId));
        logOperationalMetric(startTime, metricProcessName, "addZancaSortDeviceDetails", false, false);
    } else {
        deleteZancaSortDeviceDetails(dispatcher, mac);
        setZancaSortDeviceDetails(dispatcher, stateValues, showCustomAlert);
    }
}

export async function updateZancaSortConfiguration(dispatcher, configurations, mac, showCustomAlert) {
    // Get existing configuration
    const getAssetResult = await getAssetByAssetLabel(getAssetLabelFromMac(mac));

    // Merge current configuration into existing configuration and update
    const startTime = Date.now();
    const alias = await getAliasFromParent();
    const updatedAsset = updateConfigurationsInZancaSortAssetObject(getAssetResult.data.asset, configurations, alias);
    const updateResult = await updateAsset(updatedAsset, chainWalk(() => getAssetResult.data.asset.assetDetails.creationTime, null));
    const isUpdateSuccess = updateResult.status === 200;
    const isFault = isResponseCode5xx(updateResult.status)
    fetchZancaSortDeviceDetails(dispatcher)

    // Log operation
    isUpdateSuccess ? showCustomAlert(ALERT_UPDATE_SUCCESS_ASSET_MESSAGE) : showCustomAlert(ALERT_UPDATE_ASSET_MESSAGE);
    logOperationalMetric(startTime, metricProcessName, "updateZancaSortConfiguration", isUpdateSuccess, isFault);
}

/**
 * Updates a specific asset attribute for a ZancaSort device.
 * Synchronous method that waits for server response to enable loader UI.
 */
export async function updateAssetAttribute(dispatcher, mac, attributeName, attributeValue, showCustomAlert) {
    const startTime = Date.now();
    try {
        // Fetch existing asset
        const getAssetResult = await getAssetByAssetLabel(getAssetLabelFromMac(mac));
        const existingAsset = getAssetResult.data.asset;
        
        // Deep copy and update only the specified attribute
        const updatedAsset = deepCopyObject(existingAsset);
        const alias = await getAliasFromParent();
        updatedAsset.assetDetails.alias = chainWalk(() => alias.toString(), "");
        updatedAsset.assetDetails.assetAttributes[attributeName] = JSON.stringify(attributeValue);
        
        // Update asset
        const updateResult = await updateAsset(
            updatedAsset, 
            chainWalk(() => existingAsset.assetDetails.creationTime, null)
        );
        
        const isSuccess = updateResult.status === 200;
        const isFault = isResponseCode5xx(updateResult.status);
        
        // Refresh data and show alert
        if (isSuccess) {
            fetchZancaSortDeviceDetails(dispatcher);
            showCustomAlert(ALERT_UPDATE_SUCCESS_ASSET_MESSAGE);
        } else {
            showCustomAlert(ALERT_UPDATE_ASSET_MESSAGE);
        }
        logOperationalMetric(startTime, metricProcessName, "updateZancaSortDeviceDetails", isSuccess, isFault);
        return isSuccess;
    } catch (error) {
        showCustomAlert(ALERT_UPDATE_ASSET_MESSAGE);
        logOperationalMetric(startTime, metricProcessName, "updateZancaSortDeviceDetails", false, true);
        return false;
    }
}

async function addZancaSortDeviceDetails(dispatcher, request, showCustomAlert) {
    const startTime = Date.now();
    await setAliasToRequest(request);
    addAsset(request).then(async result => {
        if (result.status === 200) {
            logOperationalMetric(startTime, metricProcessName, "addZancaSortDeviceDetails", true, false);
        } else {
            // 500 response status can be for any reason. One of those is : asset already exists so trying to update it.
            // UpdateAsset only works on ACTIVE devices so first setting status as ACTIVE.
            const isFault = isResponseCode5xx(result.status)
            const getAssetResult = await getAssetByAssetLabel(request.assetDetails.assetLabel);
            if (!checkInactiveOrDeprecatedAsset(getAssetResult.data.asset.assetDetails.assetStatus)) {
                showCustomAlert(ALERT_UPDATE_ASSET_MESSAGE_FOR_ACTIVE_ASSET);
                logOperationalMetric(startTime, metricProcessName, "addZancaSortDeviceDetails", false, isFault);
                return;
            }
            const responseSetAssetStatus = await setAssetStatus(request.assetDetails.assetLabel, ACTIVE);
            if (responseSetAssetStatus.status !== 200) {
                showCustomAlert(ALERT_ASSET_STATUS_MESSAGE);
                logOperationalMetric(startTime, metricProcessName, "addZancaSortDeviceDetails", false, isFault);
                return;
            }
            const responseUpdateAsset = await updateAsset(request, chainWalk(() => getAssetResult.data.asset.assetDetails.creationTime, null));
            if (responseUpdateAsset.status !== 200) {
                const isFaultInUpdateAsset = isResponseCode5xx(responseUpdateAsset.status)
                showCustomAlert(ALERT_UPDATE_ASSET_MESSAGE);
                logOperationalMetric(startTime, metricProcessName, "addZancaSortDeviceDetails", false, isFaultInUpdateAsset);
                return;
            }
            logOperationalMetric(startTime, metricProcessName, "addZancaSortDeviceDetails", true, false);
        }
        fetchZancaSortDeviceDetails(dispatcher);
    });
}

async function updateZancaSortAsset(dispatcher, request, showCustomAlert) {
    // Set asset to active first
    const setAssetStatusResponse = await setAssetStatus(request.assetDetails.assetLabel, ACTIVE);
    if (setAssetStatusResponse.status !== 200) {
        showCustomAlert(ALERT_ASSET_STATUS_MESSAGE);
        fetchZancaSortDeviceDetails(dispatcher);
        return;
    }
    // Update asset details
    const startTime = Date.now();
    await setAliasToRequest(request);
    const getAssetResult = await getAssetByAssetLabel(request.assetDetails.assetLabel);
    const updateAssetResponse = await updateAsset(request, chainWalk(() => getAssetResult.data.asset.assetDetails.creationTime, null));
    const isSuccess = updateAssetResponse.status === 200;
    const isFault = isResponseCode5xx(updateAssetResponse.status)
    logOperationalMetric(startTime, metricProcessName, "updateZancaSortDeviceDetails", isSuccess, isFault);
    fetchZancaSortDeviceDetails(dispatcher);
}


async function getZancaSortDeviceDetails(dispatcher, request) {
    const model = {
        zancaSortDeviceInformation: {},
        testConfig: {},
        locationConfig: {},
        tierConfig: {},
        macAddressArray: []
    }
    // Fetch static asset details
    await getZancaSortDeviceStaticDetails(dispatcher, model, request);
    // TODO:: Fetch asset status details
}

async function getZancaSortDeviceStaticDetails(dispatcher, model, request) {
    let pageToken = null;
    // do-while loop until we get a valid pageToken
    do {
        const startTime = Date.now();
        const getAssetsResult = await getAssets(request.nodeId, request.assetType, pageToken)
        const isSuccess = getAssetsResult.status === 200;
        const isFault = isResponseCode5xx(getAssetsResult.status)

        // Log operation
        logOperationalMetric(startTime, metricProcessName, "getZancaSortDeviceDetails", isSuccess, isFault);

        if (isSuccess) {
            addZancaSortDeviceStaticDetailsToModel(getAssetsResult, model)
            // modifying pageToken to fetch from the latest get assets' result
            pageToken = getAssetsResult.data.pageToken
        } else {
            dispatcher({type: "RECEIVE_DATA", data: []});
        }
    } while (pageToken);
    dispatcher({type: "RECEIVE_DATA", data: model});
}

const addZancaSortDeviceStaticDetailsToModel = (result, model) => {
    if ("data" in result && "assetDetailsList" in result.data) {
        result.data.assetDetailsList.forEach((deviceDetail) => {
            if (deviceDetail.assetStatus === ACTIVE) {
                model.macAddressArray.push(deviceDetail.assetAttributes.mac)
                const mac = deviceDetail.assetAttributes.mac;
                const id = JSON.parse(deviceDetail.assetAttributes.deviceConfig).id;
                model.zancaSortDeviceInformation[mac] = {
                    name: deviceDetail.assetAttributes.name,
                    mac: deviceDetail.assetAttributes.mac,
                    ip: deviceDetail.assetAttributes.ip,
                    id: id,
                    port: deviceDetail.assetAttributes.port,
                    vendorAttributes: deviceDetail.vendorAttributes || DEFAULT_VENDOR_ATTRIBUTES,
                    assetStatus: LOADING_STRING,
                    deviceType: deviceDetail.assetAttributes.deviceType,
                    assetType: deviceDetail.assetType,
                    startTime: chainWalk(() => JSON.parse(deviceDetail.assetAttributes.monitoring).startTime, "0"),
                    endTime: chainWalk(() => JSON.parse(deviceDetail.assetAttributes.monitoring).endTime, "0"),
                    reason: chainWalk(() => JSON.parse(deviceDetail.assetAttributes.monitoring).reason, ""),
                    stowStationState: chainWalk(() =>
                        JSON.parse(deviceDetail.assetAttributes.stowStationState), null),
                    parentAssetLabel: deviceDetail.parentAssetLabel
                }
                model.testConfig[mac] = chainWalk(() =>
                    JSON.parse(deviceDetail.assetAttributes.testConfig, []));
                model.locationConfig[mac] = chainWalk(() =>
                    JSON.parse(deviceDetail.assetAttributes.locationConfig), []);
                model.tierConfig[mac] = chainWalk(() => {
                    const raw = JSON.parse(deviceDetail.assetAttributes.tierConfig);
                    return Object.entries(raw).map(([tierName, val]) => ({
                        tierName,
                        capacity: val.capacity,
                        startLevel: parseInt(val.levels.split(':')[0], 10),
                        endLevel: parseInt(val.levels.split(':')[1], 10)
                    }));
                }, []);
            }
        });
    }
    return model;
}

async function setAssetLabelDeprecatedZancaSortDeviceDetails(dispatcher, request) {
    const startTime = Date.now();
    setAssetStatus(request.assetLabel, request.assetStatus).then(result => {
        if (result.status === 200) {
            logOperationalMetric(startTime, metricProcessName, "deleteZancaSortDeviceDetails", true, false);
            fetchZancaSortDeviceDetails(dispatcher);
        } else {
            const isFault = isResponseCode5xx(result.status)
            logOperationalMetric(startTime, metricProcessName, "deleteZancaSortDeviceDetails", false, isFault);
            dispatcher({type: "RECEIVE_DATA", data: []});
        }
    });
}

export async function updateZancaSortMonitoringDetails(dispatcher, stateValues, mac){
    // Get existing details
    const getAssetResult = await getAssetByAssetLabel(getAssetLabelFromMac(mac));

    const updatedAsset = updateExistingAssetAttributes(getAssetResult.data.asset, stateValues);
    updateZancaSortMonitoringSchedule(dispatcher, updatedAsset)
}

const updateExistingAssetAttributes = (asset, stateValues) => {
    const updatedAsset = deepCopyObject(asset);
    updatedAsset.assetDetails.assetAttributes.monitoring =
        chainWalk(() => JSON.stringify(stateValues), asset.assetDetails.assetAttributes.monitoring)
    return updatedAsset;
}

async function updateZancaSortMonitoringSchedule(dispatcher, request) {
    // Update asset details
    const startTime = Date.now();
    const getAssetResult = await getAssetByAssetLabel(request.assetDetails.assetLabel);
    const updateAssetInPlaceResponse = await updateAssetInPlace(request,
        chainWalk(() => getAssetResult.data.asset.assetDetails.lastUpdateTime, null));
    const isSuccess = updateAssetInPlaceResponse.status === 200;
    const isFault = isResponseCode5xx(updateAssetInPlaceResponse.status)
    logOperationalMetric(startTime, metricProcessName, "updateZancaSortDeviceDetails", isSuccess, isFault);
    fetchZancaSortDeviceDetails(dispatcher);
}


// WEBPACK FOOTER //
// ./src/components/devicemanagement/zancasort/network/ZancaSortDeviceNetworkAPI.js